import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  AddressingConfigurationRuleRequestDto,
  AddressingConfigurationRuleResponseDto,
} from '@tiendanube/common';
import { useAppDispatch } from 'App/store';
import { StatusType } from 'commons/types';
import {
  getAddressingConfigurationRules,
  getAddressingConfigurationRulesStatus,
  getAddressingConfigurationRulesAction,
  saveAddressingConfigurationRulesAction,
  cleanSaveAddressingConfigurationRulesStatus,
  getAddressingConfigurationRulesSaveStatus,
} from '../../deliveryMethodsSlice';

interface UseAddressingConfigurationRulesManagementResult {
  rules: AddressingConfigurationRuleResponseDto[];
  getStatus: StatusType;
  saveStatus: StatusType;
  fetchRules: () => void;
  saveConfigurationRules: (
    params: AddressingConfigurationRuleRequestDto[],
  ) => Promise<void>;
  cleanStatus: () => void;
  isLoadingRules: boolean;
  hasError: boolean;
}

function useAddressingConfigurationRulesManagement(): UseAddressingConfigurationRulesManagementResult {
  const dispatch = useAppDispatch();
  const rules = useSelector(getAddressingConfigurationRules);
  const getStatus = useSelector(getAddressingConfigurationRulesStatus);
  const saveStatus = useSelector(getAddressingConfigurationRulesSaveStatus);

  const fetchRules = useCallback(
    () => dispatch(getAddressingConfigurationRulesAction()),
    [dispatch],
  );

  const saveConfigurationRules = useCallback(
    async (params: AddressingConfigurationRuleRequestDto[]) => {
      await dispatch(saveAddressingConfigurationRulesAction(params));
    },
    [dispatch],
  );

  const cleanStatus = useCallback(
    () => dispatch(cleanSaveAddressingConfigurationRulesStatus()),
    [dispatch],
  );

  const derivedProperties = useMemo(
    () => ({
      isLoadingRules: getStatus === 'loading',
      hasError: getStatus === 'error',
    }),
    [getStatus],
  );

  return {
    ...derivedProperties,
    rules,
    getStatus,
    saveStatus,
    fetchRules,
    saveConfigurationRules,
    cleanStatus,
  };
}

export default useAddressingConfigurationRulesManagement;
